import * as React from 'react';
import { FC, useState, useCallback, useEffect } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { TK } from '../../store/translations/translationKeys';
import { useTranslations } from '../../store/translations/hooks';
import { TextInputField, NumberInputField, SelectTextField, CheckInputField } from '../../components/Forms/fields';
import { ClientEntity } from '../../store/entities/state';
import { createPrescription, CreatePrescriptionCommand } from '../../store/entities/actions';
import { getMyProffesionalData } from '../../store/session/selectors';
import { PrescriptionData } from '../../models';
import { MedicineSearchResultData } from '../../models/api-models';
import { loadMedicines, searchMedicines } from '../../services/medicinesCache';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/DeleteRounded';
import { IconButton } from '@material-ui/core';
import { Autocomplete, TextField } from '@mui/material';
import AppButton from '../../components/AppButton';

interface PrescriptionLineForm {
    cnPem: string;
    substanciaActiva: string;
    formaFarmaceutica: string;
    embalagem: string;
    dosagem: string;
    quantidade: number;
    instrucoes: string;
}

const emptyLine = (): PrescriptionLineForm => ({
    cnPem: '',
    substanciaActiva: '',
    formaFarmaceutica: '',
    embalagem: '',
    dosagem: '',
    quantidade: 1,
    instrucoes: '',
});

interface CreatePrescriptionModalProps {
    isOpen: boolean;
    client: ClientEntity;
    onClose: () => void;
    onCreated: (prescription: PrescriptionData) => void;
}

const TIPO_RECEITA_OPTIONS = ['RN – Normal'];
const TIPO_RECEITA_VALUES: Record<string, string> = { 'RN – Normal': 'RN' };

const CreatePrescriptionModal: FC<CreatePrescriptionModalProps> = ({ isOpen, client, onClose, onCreated }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const myProfessional = useSelector(getMyProffesionalData);

    const [tipoReceita, setTipoReceita] = useState('RN – Normal');
    const [renovavel, setRenovavel] = useState(false);
    const [lines, setLines] = useState<PrescriptionLineForm[]>([emptyLine()]);
    const [saving, setSaving] = useState(false);
    const [medicineOptions, setMedicineOptions] = useState<MedicineSearchResultData[]>([]);

    useEffect(() => {
        if (isOpen) loadMedicines();
    }, [isOpen]);

    const updateLine = useCallback((index: number, patch: Partial<PrescriptionLineForm>) => {
        setLines(prev => prev.map((l, i) => i === index ? { ...l, ...patch } : l));
    }, []);

    const handleMedicineInputChange = useCallback((_: React.SyntheticEvent, value: string, index: number) => {
        updateLine(index, { substanciaActiva: value });
        if (!value.trim() || value.length < 3) { setMedicineOptions([]); return; }
        setMedicineOptions(searchMedicines(value));
    }, [updateLine]);

    const handleMedicineSelect = useCallback((medicine: MedicineSearchResultData | null, index: number) => {
        if (!medicine) return;
        updateLine(index, {
            cnPem: medicine.cnPem,
            substanciaActiva: medicine.substanciaActiva,
            formaFarmaceutica: medicine.formaFarmaceutica,
            dosagem: medicine.dosagem,
            embalagem: medicine.embalagem ?? '',
        });
    }, [updateLine]);

    const handleSubmit = async () => {
        if (!myProfessional?.id) return;
        setSaving(true);
        const command: CreatePrescriptionCommand = {
            clientId: client.id,
            professionalId: myProfessional.id,
            tipoReceita: TIPO_RECEITA_VALUES[tipoReceita] ?? 'RN',
            renovavel,
            lines: lines.map(l => ({
                cnPem: l.cnPem,
                substanciaActiva: l.substanciaActiva,
                formaFarmaceutica: l.formaFarmaceutica,
                embalagem: l.embalagem,
                dosagem: l.dosagem,
                quantidade: l.quantidade,
                instrucoes: l.instrucoes || undefined,
            })),
        };
        const result = await (dispatch as any)(createPrescription(command));
        setSaving(false);
        if (result) {
            onCreated(result);
            setLines([emptyLine()]);
            setTipoReceita('RN – Normal');
            setRenovavel(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>{t(TK.prescriptionNew)}</ModalHeader>
            <ModalBody>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                    <div style={{ display: 'flex', gap: 'var(--spacer-S)', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                            <SelectTextField
                                id="prescription-tipo-receita"
                                label={t(TK.prescriptionTipoReceita)}
                                value={tipoReceita}
                                options={TIPO_RECEITA_OPTIONS}
                                onValueChanged={setTipoReceita}
                            />
                        </div>
                        <div style={{ flexShrink: 0 }}>
                            <CheckInputField
                                id="prescription-renovavel"
                                label={t(TK.prescriptionRenovavel)}
                                value={renovavel}
                                onChange={setRenovavel}
                            />
                        </div>
                    </div>

                    {lines.map((line, index) => (
                        <div
                            key={index}
                            style={{
                                border: 'var(--border-thin) solid var(--appLigthGrey)',
                                borderRadius: 'var(--spacer-XS)',
                                padding: 'var(--spacer-S)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--spacer-S)',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <strong>{t(TK.prescriptionMedicationLine)} {index + 1}</strong>
                                {lines.length > 1 && (
                                    <IconButton size="small" onClick={() => setLines(prev => prev.filter((_, i) => i !== index))}>
                                        <RemoveCircleIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </div>
                            {/* Raw MUI Autocomplete is intentional: object options with two-line rendering
                                are not supported by AutocompleteTextField (string[] only). */}
                            <Autocomplete
                                id={`line-${index}-medicine-search`}
                                freeSolo
                                options={medicineOptions}
                                getOptionLabel={o => typeof o === 'string' ? o : o.designacao}
                                inputValue={line.substanciaActiva}
                                onInputChange={(e, v) => handleMedicineInputChange(e, v, index)}
                                onChange={(_, v) => handleMedicineSelect(typeof v === 'string' ? null : v, index)}
                                renderOption={(props, o) => (
                                    <li {...props} key={o.cnPem}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span>{o.designacao}</span>
                                            <span style={{ fontSize: '0.75em', color: 'var(--text-muted)' }}>{o.formaFarmaceutica} — {o.dosagem}</span>
                                        </div>
                                    </li>
                                )}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label={t(TK.prescriptionSubstanciaActiva)}
                                        size="small"
                                    />
                                )}
                            />
                            <TextInputField
                                id={`line-${index}-cnpem`}
                                label={t(TK.prescriptionCnPem)}
                                value={line.cnPem}
                                onChange={cnPem => updateLine(index, { cnPem })}
                            />
                            <div style={{ display: 'flex', gap: 'var(--spacer-S)' }}>
                                <div style={{ flex: 1 }}>
                                    <TextInputField
                                        id={`line-${index}-forma`}
                                        label={t(TK.prescriptionFormaFarmaceutica)}
                                        value={line.formaFarmaceutica}
                                        onChange={formaFarmaceutica => updateLine(index, { formaFarmaceutica })}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <TextInputField
                                        id={`line-${index}-dosagem`}
                                        label={t(TK.prescriptionDosagem)}
                                        value={line.dosagem}
                                        onChange={dosagem => updateLine(index, { dosagem })}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 'var(--spacer-S)' }}>
                                <div style={{ flex: 1 }}>
                                    <TextInputField
                                        id={`line-${index}-embalagem`}
                                        label={t(TK.prescriptionEmbalagem)}
                                        value={line.embalagem}
                                        onChange={embalagem => updateLine(index, { embalagem })}
                                    />
                                </div>
                                <div style={{ flexShrink: 0, width: 120 }}>
                                    <NumberInputField
                                        id={`line-${index}-quantidade`}
                                        label={t(TK.prescriptionQuantidade)}
                                        value={line.quantidade}
                                        onChange={quantidade => updateLine(index, { quantidade })}
                                    />
                                </div>
                            </div>
                            <TextInputField
                                id={`line-${index}-instrucoes`}
                                label={t(TK.prescriptionInstrucoes)}
                                value={line.instrucoes}
                                onChange={instrucoes => updateLine(index, { instrucoes })}
                            />
                        </div>
                    ))}

                    {lines.length < 4 && (
                        <AppButton
                            color="secondary"
                            outline
                            onClick={() => setLines(prev => [...prev, emptyLine()])}
                        >
                            <AddCircleIcon fontSize="small" style={{ marginRight: 'var(--spacer-XXS)' }} />
                            {t(TK.prescriptionAddLine)}
                        </AppButton>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <AppButton color="secondary" onClick={onClose} disabled={saving}>{t(TK.cancel)}</AppButton>
                <AppButton color="success" onClick={handleSubmit} disabled={saving}>{t(TK.save)}</AppButton>
            </ModalFooter>
        </Modal>
    );
};

export default CreatePrescriptionModal;
